import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styles from "./HomeSitePage.module.css";
import HomeSiteAddressList from "../components/HomeSiteAddressList";
import Header from "../components/Header";
import PurchaseButton from "../components/PurchaseButton";
import PurchasedModal from "../components/PurchasedModal";
import AddressInfoItem from "../components/AddressInfoItem";
import lockIcon from "../assets/lock.jpg";
import { useAccount } from "../utils/AccountInfoContext";
import { getDistanceFromLatLon } from "utils/getDistance";
import { useAddresses } from "AddressesPage/useAddressesPage";
import { useHomeSite } from "./useHomeSite";
import { useUsersInfo } from "./useHomeSite";
import { useTranslation } from "react-i18next";
import Loader from "components/Loader";

import Modal from "../components/Modal";
import CloseButton from "../assets/Close_Button.svg";

export function HomeSitePage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [showAddress, setShowAddress] = useState(false);
  const [showLocation, setShowLocation] = useState(false);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [purchaseActive, setPurchaseActive] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const showAddressHeader = selectedLocation || showAddress;
  const showLocationHeader = showLocation || !showAddress;

  const {
    data: homesiteData,
    isLoading,
    refetch,
    addHomesite,
  } = useHomeSite(selectedAddress?.latitude, selectedAddress?.longitude);

  const {
    data: usersData,
    refetch: refetchUserData,
    isLoading: userDataIsLoading,
    isRefetching,
  } = useUsersInfo();

  const { activeUser } = useAccount();

  const { data: addressData, isLoading: addressesIsLoading } = useAddresses();

  const [homesites, setHomesites] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [usersLocation, setUsersLocation] = useState([]);

  //addresses
  useEffect(() => {
    if (!addressesIsLoading && (!addressData || addressData?.length < 1)) {
      history.push("/");
    }
    if (addressData) {
      const formattedAddresses = addressData.map((addr) => ({
        line1: addr?.line1,
        line2: addr?.line2,
        postalCode: addr?.postalCode,
        city: addr?.city,
        state: addr?.state,
        latitude: addr?.latitude,
        longitude: addr?.longitude,
        primary: addr?.primary,
      }));
      setAddresses(formattedAddresses);

      const primaryAddress =
        formattedAddresses.find((addr) => addr.primary) ||
        formattedAddresses[0];
      if (primaryAddress) {
        setSelectedLocation(primaryAddress);
      }
    }
  }, [addressData, addressesIsLoading]);

  //homesite locations
  useEffect(() => {
    if (homesiteData) {
      const formattedHomesite = homesiteData.map((homesite) => ({
        region: homesite?.region,
        line1: homesite?.line1,
        line2: homesite?.line2,
        postalCode: homesite?.postalCode,
        city: homesite?.city,
        state: homesite?.state,
        latitude: homesite?.lat,
        longitude: homesite?.long,
        groupId: homesite?.groupId,
      }));
      setHomesites(formattedHomesite);
    }
  }, [homesiteData]);

  // Filter location based on distance to the selected address
  useEffect(() => {
    if (
      selectedLocation &&
      usersData &&
      Array.isArray(usersData[0]?.homesiteGroups) &&
      Array.isArray(homesites)
    ) {
      //First user
      const user = usersData[0];

      if (user.homesiteGroups.length > 0) {
        const sorted = user.homesiteGroups
          .map((userHomesite) => {
            //Comparing GroupIDs
            const matchingHome = homesites.find(
              (homesite) => homesite.groupId === userHomesite.groupId
            );
            if (!matchingHome) return "";

            // Calculate distances
            const distance = getDistanceFromLatLon(
              selectedLocation.latitude,
              selectedLocation.longitude,
              matchingHome.latitude,
              matchingHome.longitude
            );

            return {
              firstName: user.strFirst,
              lastname: user.strLast,
              region: userHomesite?.region,
              line1: userHomesite?.address?.line1,
              line2: userHomesite?.address?.line2,
              postalCode: userHomesite?.address?.postalCode,
              city: userHomesite?.address?.city,
              state: userHomesite?.address?.state,
              groupId: userHomesite.groupId,
              purchase: user.hasPurchasedHomesite,
              distance,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.distance - b.distance);

        setUsersLocation(sorted);

        if (sorted.length > 0) {
          setSelectedAddress(sorted[0]);
        }
      } else {
        const sortedHomesites = homesites
          .map((allHomesites) => {
            const distance = getDistanceFromLatLon(
              selectedLocation.latitude,
              selectedLocation.longitude,
              allHomesites.latitude,
              allHomesites.longitude
            );

            return { ...allHomesites, distance };
          })
          .sort((a, b) => a.distance - b.distance);

        setUsersLocation(sortedHomesites);

        // if (sortedHomesites.length > 0){
        //   setSelectedAddress(sortedHomesites[0]);
        // }
      }
    }
  }, [selectedLocation, usersData, homesites]);

  useEffect(() => {
    if (selectedAddress && selectedAddress.purchase) {
      setShowLocation(false);
      setShowAddress(false);
    }
  }, [selectedAddress]);

  const handleAddressSelection = (address) => {
    setSelectedLocation(address);
    setShowAddress(false);
    setShowLocation(true);
    setPurchaseActive(false);
  };

  const handleLocationSelection = async (location, address) => {
    const newHomesite = { groupId: location.groupId };

    try {
      await addHomesite.mutateAsync(newHomesite);
      setPurchaseActive(!purchaseActive);
      refetchUserData();
    } catch (error) {
      console.error("Failed to add homesite", error);
    }

    setSelectedAddress(location);
    setShowLocation(false);
    setPurchaseActive(false);
  };

  const handleCancel = () => {
    if (usersLocation?.length > 0) {
      setShowAddress(false);
      setShowLocation(false);
      setSelectedAddress(usersLocation[0]);
      setSelectedLocation(watchSelectedLocation);
    } else if (showLocation) {
      setShowLocation(false);
      setSelectedAddress(null);
      setShowAddress(true);
    } else if (showAddress) {
      setShowAddress(false);
      setSelectedLocation(null);
    }
  };

  const handleChangeAddress = () => {
    setSelectedLocation(null);
    setShowAddress(true);
    setShowLocation(false);
    setPurchaseActive(false);
  };

  const handleChangeLocation = () => {
    setSelectedAddress(null);
    setShowLocation(true);
    setShowAddress(false);
    setPurchaseActive(false);
  };

  const handleHeaderBackClick = () => {
    history.push("/");
  };

  const [watchSelectedAddress, setWatchSelectedAddress] = useState(null);
  const [watchSelectedLocation, setWatchSelectedLocation] = useState(null);

  //region and names are properly capitalized (not fully caps)
  const toTitleCase = (text) => {
    return text.replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
    );
  };

  useEffect(() => {
    selectedAddress && setWatchSelectedAddress(selectedAddress);
    selectedLocation && setWatchSelectedLocation(selectedLocation);
  }, [selectedAddress, selectedLocation]);

  const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);

  const handleLockClick = () => {
    setInfoModalIsOpen(true);
  };
  return (
    <div className={styles.container}>
      {isLoading || userDataIsLoading || isRefetching || addressesIsLoading ? (
        <div>
          <Loader color={"#415364"} size={85} />
        </div>
      ) : (
        <>
          {" "}
          <Header header={t("Settings")} onClick={handleHeaderBackClick} />
          <div className="content">
            <p className={styles.description}>
              {t("HomeSite is a default location for functions")}
            </p>
            <p className={styles.description}>
              {t("Be sure to counsel with your upline")}
            </p>

            <div>
              {showAddressHeader && (
                <div className={styles.addressHeader}>{t("Your Address")}</div>
              )}
              {showAddress && (
                <div>
                  <HomeSiteAddressList
                    header=""
                    addresslabel={() => toTitleCase(activeUser.name)}
                    addresses={addresses}
                    onClick={handleAddressSelection}
                    onCancel={handleCancel}
                    userHomesiteAddress={watchSelectedLocation}
                  />
                </div>
              )}

              {selectedLocation && !showAddress && (
                <>
                  <div className={styles.addressDetails}>
                    <AddressInfoItem
                      addresslabel={toTitleCase(`${activeUser?.name || ""}`)}
                      address={selectedLocation}
                    />

                    {purchaseActive ? (
                      <div className={styles.purchaseButton}>
                        <PurchaseButton
                          active={true}
                          onClick={() => setShowPurchaseModal(true)}
                        />
                      </div>
                    ) : showLocation ? null : (
                      <>
                        <button
                          className={styles.changeButton}
                          onClick={handleChangeAddress}
                          disabled={selectedLocation.purchase}
                        >
                          {t("Choose")}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div>
              {showLocationHeader && (
                <div className={styles.addressHeader}>
                  {t("HomeSite Location")}
                </div>
              )}

              {showLocation &&
                !selectedAddress?.purchase &&
                usersLocation.length > 0 &&
                homesiteData?.length > 0 && (
                  <>
                    <HomeSiteAddressList
                      addresslabel={(homesiteData) =>
                        toTitleCase(homesiteData.region)
                      }
                      addresses={homesiteData}
                      onClick={handleLocationSelection}
                      onCancel={handleCancel}
                      userHomesiteAddress={watchSelectedAddress}
                    />
                  </>
                )}

              {!showLocation && !showAddress && (
                <>
                  <div className={styles.addressDetails}>
                    <AddressInfoItem
                      addresslabel={
                        selectedAddress?.region
                          ? toTitleCase(selectedAddress?.region)
                          : "No HomeSite Selected"
                      }
                      address={selectedAddress}
                    />
                    <Modal
                      className={styles.modal}
                      isOpen={infoModalIsOpen}
                      children={
                        <div className={styles.modalContent}>
                          <div className={styles.modalCloseButtonContainer}>
                            <PurchaseButton
                              active={false}
                              className={styles.modalPurchaseIcon}
                            />
                            <img
                              src={CloseButton}
                              onClick={() => setInfoModalIsOpen(false)}
                              className={styles.modalCloseButton}
                            />
                          </div>
                          <div className={styles.modalText}>
                            <div className={styles.modalHeader}>
                              HomeSite Locked
                            </div>
                            <div className={styles.modalDescription}>
                              You have already purchased tickets for your
                              HomeSite location. You cannot change your HomeSite
                              until that event is over.
                            </div>
                          </div>
                        </div>
                      }
                    />

                    {purchaseActive ? (
                      <div className={styles.purchaseButton}>
                        <PurchaseButton
                          active={true}
                          onClick={() => setShowPurchaseModal(true)}
                        />
                      </div>
                    ) : (
                      <>
                        {selectedAddress?.purchase ? (
                          <img
                            src={lockIcon}
                            alt="Locked"
                            className={styles.lockIcon}
                            onClick={() => handleLockClick()}
                          />
                        ) : (
                          <button
                            className={styles.changeButton}
                            onClick={handleChangeLocation}
                          >
                            {t("Choose")}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <PurchasedModal
            isOpen={showPurchaseModal}
            onClose={() => setShowPurchaseModal(false)}
          />
        </>
      )}
    </div>
  );
}

export default HomeSitePage;
